import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FileText, AlertTriangle, Lightbulb, Network } from "lucide-react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import KGraph from "@/components/graph/CytoGraph";
import { WorkspaceHeaderPanel } from "@/components/workspaces/WorkspaceHeaderPanel";
import { ArtifactsTab } from "@/components/workspaces/ArtifactsTab";
import { FindingsTab } from "@/components/workspaces/FindingsTab";
import { RecommendationsTab } from "@/components/workspaces/RecommendationsTab";
import { RecommendationDrawer } from "@/components/workspaces/RecommendationDrawer";

export default function WorkspaceDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [ws, setWs] = useState(null);
    const [artifacts, setArtifacts] = useState([]);
    const [findings, setFindings] = useState([]);
    const [recs, setRecs] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [archiveReport, setArchiveReport] = useState([]);
    const [openRec, setOpenRec] = useState(null);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const [a, b, c, d] = await Promise.all([
                api.get(`/workspaces/${id}`),
                api.get(`/workspaces/${id}/artifacts`),
                api.get("/findings", { params: { workspace_id: id } }),
                api.get("/recommendations", { params: { workspace_id: id } }),
            ]);
            setWs(a.data);
            setArtifacts(b.data);
            setFindings(c.data);
            setRecs(d.data);
        } catch (e) {
            toast.error("Failed to load workspace");
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const upload = async (files) => {
        if (!files?.length) return;
        const form = new FormData();
        for (const f of files) form.append("files", f);
        setUploading(true);
        try {
            const { data } = await api.post(`/workspaces/${id}/artifacts/upload`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const archives = data.archives || [];
            setArchiveReport(archives);
            if (archives.length) {
                const unpacked = archives.reduce((s, a) => s + a.members_registered, 0);
                const skipped = archives.reduce((s, a) => s + (a.skipped?.length || 0), 0);
                toast.success(
                    `Unpacked ${archives.length} archive(s) — ${unpacked} file(s) registered` +
                    (skipped ? `, ${skipped} skipped` : "")
                );
            } else {
                toast.success(`Uploaded ${data.uploaded.length} artifact(s)`);
            }
            await load();
        } catch (e) {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        upload(Array.from(e.dataTransfer.files || []));
    };

    const runAnalysis = async () => {
        setAnalyzing(true);
        try {
            const { data } = await api.post(`/workspaces/${id}/analyze`);
            toast.success(`Analysis complete: ${data.findings} findings`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Analysis failed");
        } finally {
            setAnalyzing(false);
        }
    };

    const review = async (rid, action) => {
        try {
            const { data } = await api.post(`/recommendations/${rid}/review`, { action, comment: "" });
            const captured = data?.impact_evidence?.status === "captured";
            toast.success(
                `Recommendation ${action}d${captured ? " · Change Impact Brief attached" : ""}`,
            );
            await load();
            setOpenRec(data);
        } catch (e) {
            toast.error("Review failed");
        }
    };

    const explain = async (rid) => {
        try {
            toast.message("Asking ASSEP reasoner…");
            const { data } = await api.post(`/recommendations/${rid}/explain`);
            if (data.explanation) {
                toast.success("Explanation refreshed");
                await load();
                const fresh = (await api.get(`/recommendations/${rid}`)).data;
                setOpenRec(fresh);
            } else {
                toast.error("No explanation generated");
            }
        } catch (e) {
            toast.error("Explanation failed");
        }
    };

    const deleteArtifact = async (a) => {
        try {
            await api.delete(`/artifacts/${a.id}`);
            toast.success(`Removed ${a.filename}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to remove artifact");
        }
    };

    if (!ws) return <div className="text-slate-500 font-mono text-xs">Loading workspace…</div>;

    return (
        <div className="space-y-6">
            <WorkspaceHeaderPanel ws={ws} nav={nav} runAnalysis={runAnalysis} analyzing={analyzing} artifactsLength={artifacts.length} />

            <Tabs defaultValue="artifacts" className="w-full">
                <TabsList className="bg-[var(--bg-surface-2)] border border-white/5 p-1 h-auto">
                    {[
                        ["artifacts", "Artifacts", FileText, artifacts.length],
                        ["findings", "Findings", AlertTriangle, findings.length],
                        ["recommendations", "Recommendations", Lightbulb, recs.length],
                        ["graph", "Knowledge Graph", Network, null],
                    ].map(([v, l, Icon, n]) => (
                        <TabsTrigger
                            key={v}
                            value={v}
                            className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-300 data-[state=active]:shadow-none text-slate-400 px-4 py-2 text-xs font-mono uppercase tracking-wider rounded-sm flex items-center gap-2"
                        >
                            <Icon size={12} /> {l}
                            {n !== null && <span className="text-[10px] text-slate-500">({n})</span>}
                        </TabsTrigger>
                    ))}
                </TabsList>

                <TabsContent value="artifacts" className="mt-5">
                    <ArtifactsTab
                        artifacts={artifacts}
                        uploading={uploading}
                        dragOver={dragOver}
                        setDragOver={setDragOver}
                        handleDrop={handleDrop}
                        fileRef={fileRef}
                        upload={upload}
                        deleteArtifact={deleteArtifact}
                        workspaceId={id}
                        archiveReport={archiveReport}
                        onIngested={load}
                    />
                </TabsContent>

                <TabsContent value="findings" className="mt-5">
                    <FindingsTab findings={findings} />
                </TabsContent>

                <TabsContent value="recommendations" className="mt-5">
                    <RecommendationsTab recs={recs} setOpenRec={setOpenRec} />
                </TabsContent>

                <TabsContent value="graph" className="mt-5">
                    <KGraph workspaceId={id} />
                </TabsContent>
            </Tabs>

            <RecommendationDrawer openRec={openRec} setOpenRec={setOpenRec} review={review} explain={explain} />
        </div>
    );
}
